import { useAuth } from '@/auth/AuthContext';
import { TradingEventLogProvider } from '@/contexts/TradingEventLogContext';
import { useLocale } from '@/i18n/LocaleContext';
import { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import './Layout.css';

const MARKET_ROUTES = [
  { path: '/opp', labelKey: 'sub.oppTop' as const },
  { path: '/opp-funding-hedge', labelKey: 'sub.fundingHedge' as const },
  { path: '/opp-mid-deviation', labelKey: 'sub.midDeviation' as const },
  { path: '/kline-screener', labelKey: 'sub.klineScreener' as const },
];

const MARKET_PATHS = MARKET_ROUTES.map((route) => route.path);

function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const initial = (user?.username ?? '?').slice(0, 1).toUpperCase();

  return (
    <div className="layout-user-menu" ref={rootRef}>
      <button
        type="button"
        className="layout-user-menu-trigger"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{t('nav.userInfo')}</span>
        <span className="layout-user-avatar" aria-hidden="true">
          {initial}
        </span>
      </button>
      {open && (
        <div className="layout-user-dropdown" role="menu">
          <Link to="/trading-settings" role="menuitem" onClick={() => setOpen(false)}>
            {t('nav.accountSettings')}
          </Link>
          <Link to="/system-api-keys" role="menuitem" onClick={() => setOpen(false)}>
            {t('nav.api')}
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

function LanguageToggle() {
  const { locale, setLocale } = useLocale();

  return (
    <div className="layout-lang-toggle" role="group" aria-label="Language">
      <button
        type="button"
        className={locale === 'zh' ? 'active' : ''}
        onClick={() => setLocale('zh')}
      >
        中
      </button>
      <button
        type="button"
        className={locale === 'en' ? 'active' : ''}
        onClick={() => setLocale('en')}
      >
        EN
      </button>
    </div>
  );
}

export function Layout() {
  const location = useLocation();
  const { t } = useLocale();

  const isMarketSection = MARKET_PATHS.includes(location.pathname);
  const isTradingSection = location.pathname === '/trading';
  const isAccountPanelActive = location.pathname === '/account-overview';

  return (
    <TradingEventLogProvider>
      <div className="layout">
      <header className="layout-header">
        <div className="layout-header-row">
          <div className="layout-header-side layout-header-side-left">
            <Link to="/landing" className="layout-brand" aria-label="ArbX landing">
              <span className="layout-brand-mark">A</span>
              <span>ArbX</span>
            </Link>
          </div>

          <nav className="layout-primary-nav" aria-label="Main">
            <Link to="/opp" className={isMarketSection ? 'active' : ''}>
              {t('nav.market')}
            </Link>
            <Link to="/trading" className={isTradingSection ? 'active' : ''}>
              {t('nav.trading')}
            </Link>
            <span className="layout-nav-placeholder" title={t('nav.botSoon')}>
              {t('nav.bot')}
            </span>
          </nav>

          <div className="layout-header-side layout-header-side-right">
            <div className="layout-user-area">
            <Link
              to="/account-overview"
              className={`layout-account-panel${isAccountPanelActive ? ' active' : ''}`}
            >
              {t('nav.accountPanel')}
            </Link>
            <UserMenu />
            <LanguageToggle />
            </div>
          </div>
        </div>
      </header>
      <main className={`layout-main${isMarketSection ? ' has-market-sidebar' : ''}`}>
        {isMarketSection ? (
          <nav className="layout-market-sidebar" aria-label="Market sections">
            {MARKET_ROUTES.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className={location.pathname === route.path ? 'active' : ''}
              >
                {t(route.labelKey)}
              </Link>
            ))}
          </nav>
        ) : null}
        <div className="layout-main-content">
          <Outlet />
        </div>
      </main>
      </div>
    </TradingEventLogProvider>
  );
}
